/**
 * Das Archiv (ADR 0025): alle Geräte mit archivierendem Status, für alle
 * sichtbar.
 *
 * Je archivierendem Status ein Filter. Der gewählte steht in der Adresse
 * (`?status=verloren`), damit er nach dem Zurück wieder da ist. Ein Tipp
 * öffnet die Geräteseite, dort lässt sich das Gerät weiter pflegen.
 *
 * Aus der lokalen Kopie, also auch offline.
 */

import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { archivFilter, archivierte } from "../lokal/archiv.js";
import { useBestand } from "../lokal/bestand.js";
import { Farbpunkt, Ladeanzeige, Leer, Nummer, StatusPunkt } from "../ui/bausteine.jsx";

function datum(iso: string | null): string | null {
  if (iso === null) return null;
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function Filterknopf({
  an,
  onClick,
  children,
}: {
  an: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={an}
      onClick={onClick}
      className={`flex min-h-12 items-center gap-2 rounded-full px-4 font-semibold ${
        an
          ? "bg-blue-700 text-white active:bg-blue-800"
          : "bg-white active:bg-neutral-200 dark:bg-neutral-800 dark:active:bg-neutral-700"
      }`}
    >
      {children}
    </button>
  );
}

export function Archiv() {
  const bestand = useBestand();
  const [parameter, setParameter] = useSearchParams();
  const gewaehlt = parameter.get("status");

  const zeilen = useMemo(() => (bestand === undefined ? [] : archivierte(bestand)), [bestand]);
  const filter = useMemo(
    () => (bestand === undefined ? [] : archivFilter(bestand, zeilen)),
    [bestand, zeilen],
  );

  if (bestand === undefined) return <Ladeanzeige was="Das Archiv" />;

  const sichtbar = gewaehlt === null ? zeilen : zeilen.filter((z) => z.zustand.status === gewaehlt);
  const waehlen = (status: string | null): void => {
    // Ersetzen statt neuer Eintrag: Das Zurück führt aus dem Archiv hinaus,
    // nicht durch jeden Filter.
    setParameter(status === null ? {} : { status }, { replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Archiv</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Geräte mit archivierendem Status fallen aus allen Listen. Hier stehen sie, das zuletzt
        gebuchte zuerst.
      </p>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Nach Status filtern">
        <Filterknopf
          an={gewaehlt === null}
          onClick={() => {
            waehlen(null);
          }}
        >
          Alle {zeilen.length}
        </Filterknopf>
        {filter.map((f) => (
          <Filterknopf
            key={f.status.schluessel}
            an={gewaehlt === f.status.schluessel}
            onClick={() => {
              waehlen(f.status.schluessel);
            }}
          >
            <Farbpunkt farbe={f.status.farbe} />
            {f.status.bezeichnung} {f.anzahl}
          </Filterknopf>
        ))}
      </div>

      {sichtbar.length === 0 ? (
        <Leer>{zeilen.length === 0 ? "Das Archiv ist leer." : "Mit diesem Status nichts."}</Leer>
      ) : (
        <ul className="flex flex-col gap-2">
          {sichtbar.map(({ artikel, zustand }) => {
            const ort =
              zustand.lagerort_id === null ? undefined : bestand.lagerorte.get(zustand.lagerort_id);
            const seit = datum(zustand.letzte_buchung_am);
            return (
              <li key={artikel.id}>
                <Link
                  to={`/artikel/${artikel.id}`}
                  className="flex min-h-16 flex-col justify-center gap-1 rounded-xl bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800 dark:active:bg-neutral-700"
                >
                  <span className="font-semibold">{artikel.bezeichnung}</span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Nummer>{artikel.inventarnummer}</Nummer>
                    <StatusPunkt status={zustand.status} />
                    {seit !== null && (
                      <span className="text-sm text-neutral-600 dark:text-neutral-300">
                        zuletzt gebucht {seit}
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-300">
                    {ort === undefined ? "Ort unbekannt" : `${ort.kennung} — ${ort.bezeichnung}`}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
